/**
 * PaymentsPipelineBoard.tsx — the admin PAYMENTS money board: the Card view of
 * the merged Payments section (UX Wave 3b; born as CRM PR 5/5's "Payments
 * pipeline" tab lead).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 3b (Payments merge).
 *
 * WHY a second board: the deals Pipeline answers "where is this PERSON in
 * their journey"; this one answers "where is the MONEY" — four columns
 * derived server-side from `SubscriptionState` (ongoing / failed / cancelled
 * / completed — see orders-service dto.ts for the mapping and the money
 * semantics). The board renders EXACTLY what the server groups: no
 * client-side re-bucketing, so the two can never disagree. Wave 3b's CTO rule
 * ("cards move to the right status") means a manual DRAG must map to a real
 * owning action — the cancel/retry affordances need orders-side endpoints, so
 * this slice ships NO drag at all rather than a freeform state overwrite.
 *
 * Wave 3b enrichment (extended Wave 6 §3): each card leads with WHO — the
 * patient's NAME headline with their EMAIL on a secondary line, via ONE
 * batched identity contact lookup over every visible patientUserId (never one
 * per card; a missing name falls back to the email as the headline, a missing
 * contact to the raw id) — and WHAT: the medication NAME resolved from the
 * clinic's plan-options read (the same C2.1 read the patient plan chooser
 * uses; falls back to the raw productKey while options load or if the read
 * fails).
 *
 * Money surface rules: ADMIN-only (the route 403s everyone else — R41); every
 * figure is integer pence formatted at the edge. The FAILED lane leads with
 * the most-retried renewal (server-ordered) — that is the card an operator
 * must act on first, and its header chip carries the lane's total stuck
 * money. A card click hands the subscription to the SAME drill-down drawer
 * the list view uses (one detail surface, never two).
 *
 * ⚠️ THERE IS NO MARK-AS-TEST CONTROL ON THIS BOARD (removed 2026-09-05).
 *
 * It was added here on 2026-09-04 because this board is where a test
 * transaction gets NOTICED. That was true, and it still produced the wrong
 * shape: a human could then mark a test record in THREE independent places —
 * the deal, the patient directory and here — as three unrelated actions over
 * three different halves of one record. They disagreed constantly, and a
 * half-marked record is worse than an unmarked one, because it is hidden on
 * whichever board was marked and counted on every board that was not.
 *
 * The ROUTINE path is the DEAL: clinic-service's cascade marks the deal, its
 * plan and its patient in ONE action, so the deal drawer is the single place a
 * human starts it and the duplicate entry points are gone. This board is a READ
 * of the money again.
 *
 * ⚠️ TENSE — THAT CASCADE IS ON `dev` (verified 2026-09-05: clinic `dev` =
 * 9fb728c, orders `dev` = 46784b3, identity `dev` = fdc9d21; this block said
 * "not merged" until then, which was true when written and stale a day
 * later). The PATIENTS screen keeps a scoped ADMIN UN-MARK for the PATIENT
 * flag in its record drawer — the undo direction only — but no longer because
 * nothing else can clear it: the cascade WITHHOLDS the human leg whenever the
 * patient's other deals disagree, so that cohort has no other way back. ⚠️
 * THAT ARGUMENT STILL DOES NOT REACH THIS BOARD. The ORDER-CASE flag this
 * board used to write has no withholding rule over it: the deal route writes
 * it and clears it unconditionally (orders audits the reversal as
 * `order_unmarked_test`), so nothing here is stranded and nothing needs
 * restoring. And it is not a precedent for a MARK either — the Patients screen
 * offers none, on any surface (see `pms/PatientRecordDrawer.tsx`).
 *
 * ⚠️ THERE IS NOW A "show test records" FILTER, AND THE OLD PROHIBITION AGAINST
 * ONE WAS DISCHARGED RATHER THAN OVERRULED (2026-09-05).
 *
 * This block used to forbid it: "there is nothing on this wire to filter ON…
 * a control that silently does nothing is worse than the absence it replaces",
 * and it named the exact server change that would unblock it — "an `includeTest`
 * query param plus `isTest` on the card". orders-service has shipped both, so
 * the facet reaches the server and the panel is real work rather than theatre.
 * ⚠️ IF THE ORDERS SIDE IS EVER REVERTED, THE PANEL GOES BACK OUT WITH IT.
 *
 * ⚠️ THE AUDIT VIEW'S TOTALS INCLUDE THE DRY-RUN MONEY, and the board says so
 * out loud when it is on. orders-service builds every rollup as Σ of the cards
 * it returns — a header chip that disagreed with the cards beneath it would be
 * unreadable — so "how much of this board is fake?" is answered by diffing the
 * two views, not by reading one of them. An admin who took a secured total out
 * of this view as revenue would be quoting rehearsal money, so the note under
 * the rollups is not decoration.
 *
 * ⚠️ IT IS STILL A READ, AND STILL NOT A MARK. Seeing the flagged cases here
 * does not put the mark-as-test control back on this board — the block above
 * explains why the DEAL is the single place a human starts that cascade.
 */
import { useCallback, useEffect, useState } from 'react';
import { lookupStaffContactMap, type StaffContact } from '../api/identity';
import { getPaymentsPipeline } from '../api/orders';
import type { PaymentsPipelineCard, PaymentsPipelineColumn, PaymentsPipelineResponse } from '../api/types';
import { formatMonthly, formatPence, formatUtcDate } from '../lib/format';
import { SectionTitle } from '../staff/ui';
import { labelFor, toneFor } from '../ui/tones';
import { Badge, Card, ErrorState, Help } from '../ui/kit';
import { BoardSkeleton } from '../ui/skeletons';
/* ⚠️ THE FILTER CONTROL IS NO LONGER THIS BOARD'S (CTO, 2026-09-06) — it lives
   on PaymentsSection's view-toggle row, so the board is handed its applied
   state and owns none of the furniture. The estate's shared grammar
   (ui/filters.tsx) is still what renders it; it is just mounted one level up.
   See the block on the `filters` prop below. */
import type { PaymentsFilterState } from './PaymentsFilters';

/** Operator-facing column titles — plain words, not state-machine names. */
const COLUMN_LABELS: Record<PaymentsPipelineColumn['key'], string> = {
  ongoing: 'Ongoing',
  failed: 'Failed',
  cancelled: 'Cancelled',
  completed: 'Completed',
};

export function PaymentsPipelineBoard({
  medicationNames,
  onOpenSubscription,
  filters,
}: {
  /** productKey → medication name (the clinic plan-options read, owned by the section). */
  medicationNames: Record<string, string>;
  /** Card click → the shared subscription drill-down drawer (owned by the section). */
  onOpenSubscription: (subscriptionId: string, title: string) => void;
  /**
   * The APPLIED filter state, owned by PaymentsSection.
   *
   * ⚠️ A PROP, NOT LOCAL STATE, AND THAT IS THE WHOLE POINT OF THE MOVE. The
   * control now sits on the section's view-toggle row (empty space that was
   * already being paid for), and a control cannot live above the component that
   * owns the state it sets. Lifting the state is what buys the placement.
   *
   * It stays the READ's dependency, not a client-side predicate: the flagged
   * cases are not in the default response at all, so a change here has to
   * re-ask the server.
   */
  filters: PaymentsFilterState;
}) {
  const [data, setData] = useState<PaymentsPipelineResponse | null>(null);
  const [patientContacts, setPatientContacts] = useState<Record<string, StaffContact>>({});
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const showTest = filters.showTest;
  const load = useCallback(() => {
    setStatus('loading');
    // `showTest` is a dependency of the READ, not a client-side predicate: the
    // flagged cases are not in the default response at all, so there is nothing
    // to filter locally and re-asking the server is the only way to see them.
    getPaymentsPipeline(showTest)
      .then(async (d) => {
        setData(d);
        setStatus('ready');
        // ONE batched lookup for every visible patientUserId — never one per card.
        const ids = Array.from(new Set(d.columns.flatMap((c) => c.cards.map((card) => card.patientUserId)).filter(Boolean)));
        if (ids.length === 0) return;
        try {
          setPatientContacts(await lookupStaffContactMap(ids));
        } catch {
          // Best-effort — cards fall back to the raw patientUserId if the lookup fails.
        }
      })
      .catch(() => setStatus('error'));
  }, [showTest]);

  useEffect(() => {
    load();
  }, [load]);

  const empty = data !== null && data.columns.every((c) => c.cards.length === 0);

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-4)' }}>
      {/* ⚠️ THE TOOLBAR THAT USED TO BE HERE IS NOW ON THE SECTION'S VIEW ROW
          (CTO, 2026-09-06), AND THE INVARIANT IT CARRIED IS STRONGER FOR IT.

          The rule was: the bar must render in all three states, OUTSIDE the
          loading/error/ready branches below — the dead end the Patients
          directory hit and wrote up. If the bar only existed on the success
          branch, a read that FAILED in the audit view would take away the only
          control that can turn the audit view off, leaving a Retry that
          re-fires the same failing request.

          That is no longer something this file has to remember. The control is
          mounted by PaymentsSection, a level above every branch here, so it
          cannot be swallowed by one of them even by accident. The board's body
          switches; its controls are not its to lose.

          The other rule — CHIPS IN THE ROW, NEVER ON ONE OF THEIR OWN — moved
          with it and is enforced at the new site. */}

      {status === 'loading' ? (
        // Wave 6 §1: board-shaped loading — one skeleton lane per money column.
        <BoardSkeleton lanes={4} label="Loading the payments board…" />
      ) : status === 'error' || data === null ? (
        <ErrorState message="Sorry — we could not load the payments board." onRetry={load} />
      ) : (
        <>
      {/* The company money position — the three whole-board rollups. */}
      <div role="group" aria-label="Money position" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--gps-s-3)' }}>
        <MoneyStat label="Secured so far" pence={data.totals.securedPence} />
        <MoneyStat label="Estimated remaining" pence={data.totals.estimatedRemainingPence} />
        <MoneyStat label="Stuck" pence={data.totals.stuckPence} tone={data.totals.stuckPence > 0 ? 'danger' : undefined} />
      </div>

      {/* ⚠️ SAID WHERE THE NUMBERS ARE, NOT ONLY IN THE PANEL THAT SET IT. The
          rollups are Σ of the cards returned, so in the audit view they COUNT
          the dry-run money — and the person reading a secured total minutes
          later is not the person who ticked the box. */}
      {showTest ? (
        <Help style={{ marginBottom: 0 }}>
          Test records are on the board, and these totals include their money. Turn the filter off for
          the real position.
        </Help>
      ) : null}

      {empty ? (
        <Help style={{ marginBottom: 0 }}>No subscriptions yet — cards appear when a patient checks out.</Help>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 'var(--gps-s-3)', alignItems: 'start' }}>
          {data.columns.map((column) => (
            <section
              key={column.key}
              aria-label={`${COLUMN_LABELS[column.key]} payments`}
              style={{
                background: 'var(--gps-surface-2, var(--gps-bg))',
                border: '1px solid var(--gps-border)',
                borderRadius: '10px',
                padding: 'var(--gps-s-3)',
                display: 'grid',
                gap: 'var(--gps-s-3)',
                alignContent: 'start',
              }}
            >
              <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
                <h3 style={{ margin: 0, fontSize: '14.5px', color: 'var(--gps-text)' }}>
                  {COLUMN_LABELS[column.key]} <span style={{ color: 'var(--gps-muted)', fontWeight: 400 }}>({column.cards.length})</span>
                </h3>
                {/* The lane's operative number: stuck money on Failed, secured elsewhere. */}
                {column.key === 'failed' ? (
                  column.totals.stuckPence > 0 ? <Badge tone="danger">{formatPence(column.totals.stuckPence)} stuck</Badge> : null
                ) : column.totals.securedPence > 0 ? (
                  <Badge tone="muted">{formatPence(column.totals.securedPence)} secured</Badge>
                ) : null}
              </header>

              {column.cards.length === 0 ? (
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--gps-muted)' }}>Nothing here.</p>
              ) : (
                column.cards.map((card) => (
                  <PipelineCard
                    key={card.subscriptionId}
                    card={card}
                    contact={patientContacts[card.patientUserId]}
                    medicationName={medicationNames[card.productKey]}
                    onOpen={onOpenSubscription}
                  />
                ))
              )}
            </section>
          ))}
        </div>
      )}
        </>
      )}

    </div>
  );
}

/** One whole-board rollup figure. */
function MoneyStat({ label, pence, tone }: { label: string; pence: number; tone?: 'danger' }) {
  return (
    <Card style={{ padding: 'var(--gps-s-3)', minWidth: '150px', flex: '1 1 150px' }}>
      <SectionTitle>{label}</SectionTitle>
      <span
        style={{
          fontSize: '20px',
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          color: tone === 'danger' ? 'var(--gps-danger)' : 'var(--gps-text)',
        }}
      >
        {formatPence(pence)}
      </span>
    </Card>
  );
}

/** One subscription card: WHO + WHAT + state, then the four money lines. */
function PipelineCard({
  card,
  contact,
  medicationName,
  onOpen,
}: {
  card: PaymentsPipelineCard;
  /** Resolved patient contact; undefined while loading / on failure → raw id. */
  contact?: StaffContact;
  /** Resolved medication name; undefined while loading / on failure → raw productKey. */
  medicationName?: string;
  onOpen: (subscriptionId: string, title: string) => void;
}) {
  // Wave 6 §3: NAME headline, EMAIL secondary. No stored name → the email IS
  // the headline (no duplicate secondary line); no contact at all → the raw id.
  const patient = contact ? contact.fullName ?? contact.email : card.patientUserId;
  const secondaryEmail = contact?.fullName ? contact.email : null;
  const plan = `${medicationName ?? card.productKey} (${card.months} mo)`;
  return (
    <button
      type="button"
      onClick={() => onOpen(card.subscriptionId, plan)}
      aria-label={`Open subscription ${patient} — ${plan}`}
      style={{
        display: 'grid',
        gap: '6px',
        width: '100%',
        minHeight: '44px',
        textAlign: 'left',
        background: 'var(--gps-bg)',
        border: '1px solid var(--gps-border)',
        borderRadius: '8px',
        padding: '10px',
        cursor: 'pointer',
        font: 'inherit',
      }}
    >
      {/* WHO leads (Wave-2 "name, not email"), with the state chip alongside. */}
      <span style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center' }}>
        {/* STRUCK THROUGH ON A FLAGGED CARD — the treatment the pipeline card and
            the Patients row already give a marked record. The strikethrough is on
            the NAME because the name is what an operator scans; it is decoration
            on top of the chip beside it, never the only cue (WCAG 1.4.1). */}
        <span
          style={{
            fontWeight: 600,
            fontSize: '13.5px',
            color: 'var(--gps-text)',
            overflowWrap: 'anywhere',
            ...(card.isTest === true ? { textDecoration: 'line-through' } : {}),
          }}
        >
          {patient}
        </span>
        {/* The MUTED chip, verbatim from the Patients directory and the pipeline
            card. Muted is the point: an operator must tell a rehearsal from real
            money at a glance without it shouting over the state chip beside it.
            Rendered on the FIELD, never on the request we sent — orders-service
            puts `isTest` on every card in both modes, so the badge cannot get out
            of step with the view. `=== true` because an orders revision that
            predates the field sends nothing, and absent is not "not a test". */}
        {card.isTest === true ? <Badge tone="muted">Test</Badge> : null}
        <Badge tone={toneFor(card.state)}>{labelFor(card.state)}</Badge>
      </span>
      {/* Wave 6 §3: the email rides secondary under the name headline. */}
      {secondaryEmail ? (
        <span style={{ fontSize: '12px', color: 'var(--gps-muted)', overflowWrap: 'anywhere' }}>{secondaryEmail}</span>
      ) : null}

      {/* WHAT: the medication name + term, then the billing progress line. */}
      <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gps-text)' }}>{plan}</span>
      <span style={{ fontSize: '12.5px', color: 'var(--gps-muted)' }}>
        {formatMonthly(card.monthlyPence)} · {card.monthsElapsed}/{card.months} months billed
      </span>

      {card.awaitingFirstPayment ? <Badge tone="pending">Awaiting first payment</Badge> : null}
      {card.retryCount > 0 ? <Badge tone="danger">{card.retryCount} failed {card.retryCount === 1 ? 'attempt' : 'attempts'}</Badge> : null}

      <span style={{ display: 'grid', gap: '2px', fontSize: '12.5px', fontVariantNumeric: 'tabular-nums' }}>
        <MoneyLine label="Lifetime value" pence={card.ltvPence} />
        <MoneyLine label="Secured" pence={card.securedPence} />
        <MoneyLine label="Est. remaining" pence={card.estimatedRemainingPence} />
        {card.stuckPence > 0 ? <MoneyLine label="Stuck" pence={card.stuckPence} danger /> : null}
      </span>

      {card.nextPaymentDate ? (
        <span style={{ fontSize: '12px', color: 'var(--gps-muted)' }}>Next payment {formatUtcDate(card.nextPaymentDate)}</span>
      ) : null}
    </button>
  );
}

function MoneyLine({ label, pence, danger }: { label: string; pence: number; danger?: boolean }) {
  return (
    <span style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
      <span style={{ color: 'var(--gps-muted)' }}>{label}</span>
      <span style={{ fontWeight: 600, color: danger ? 'var(--gps-danger)' : 'var(--gps-text)' }}>{formatPence(pence)}</span>
    </span>
  );
}
